import { memo, useCallback, useMemo, useRef } from 'react';

import useOrderFilterValues from 'api/useOrderFilterValues';
import { URL_ARRAY_PARAM_SEPARATOR } from 'constants/constants';
import { EOrderStatus } from 'constants/enums';
import { usePaginationFromURL } from 'hooks/usePaginationFromURL';

import { FilterPopover } from 'components/FilterComponents/FilterPopover/FilterPopover';
import { FilterType } from 'components/FilterComponents/FilterPopover/types';

import { OrderFilterKey, VALID_ORDER_STATUSES } from '../../constant';
import { createStatusFilterOptions } from '../../utils';

interface StatusFilterProps {
  isFree?: boolean;
  selectedProjects: string;
  handleUpdateParam: (paramName: string, paramValue: string) => void;
}

export const StatusFilter = memo(
  ({ isFree, selectedProjects, handleUpdateParam }: StatusFilterProps) => {
    const { statuses } = usePaginationFromURL('orders');

    const { data: filterValues } = useOrderFilterValues(selectedProjects);

    const handleUpdateParamRef = useRef(handleUpdateParam);

    handleUpdateParamRef.current = handleUpdateParam;

    const statusOptions = useMemo(() => {
      const states = filterValues?.states;
      const validStatuses = states?.length
        ? (states as EOrderStatus[])
        : VALID_ORDER_STATUSES;
      return createStatusFilterOptions(validStatuses, isFree);
    }, [filterValues?.states, isFree]);

    const filterOptions = useMemo(
      () =>
        statusOptions.map((status) => ({
          label: status.text,
          value: status.statuses
        })),
      [statusOptions]
    );

    const handleStatusesSelectionChange = useCallback(
      (selectedItems: string[]) => {
        if (selectedItems.length === 0) {
          handleUpdateParamRef.current(OrderFilterKey.STATUSES, '');
          return;
        }

        const encodedStatuses = encodeURIComponent(
          selectedItems.join(URL_ARRAY_PARAM_SEPARATOR)
        );
        handleUpdateParamRef.current(OrderFilterKey.STATUSES, encodedStatuses);
      },
      []
    );

    return (
      <FilterPopover
        label="Status"
        options={filterOptions}
        selectedItems={statuses}
        onSelectionChange={handleStatusesSelectionChange}
        filterType={FilterType.AUTOCOMPLETE_CHECKLIST}
        popoverHeight="auto"
        placeholder="Search Status..."
      />
    );
  }
);
